import React from 'react';
import styled from 'styled-components';

import { SERVICE_TYPE_ICONS } from '@rover/components/src/formFields/ServiceTypeFilter/ServiceTypeFilter';
import { Box, Flex } from '@rover/kibble/core';
import type { ServiceTypeOption, ServiceTypeSlug } from '@rover/types/src/ServiceType';

import TruncatedText from '../../TruncatedText';

type Props = {
  options: ServiceTypeOption[];
  value: ServiceTypeSlug;
  onChange: (serviceTypeSlug: ServiceTypeSlug) => void;
  iconColor: string;
  focusedIndex?: number;
  getOptionId?: (optionIndex: number) => string;
};

const OptionButton = styled.button<{ $isSelected: boolean; $isFocused: boolean }>`
  width: 100%;
  padding: ${({ theme }) => theme.space['3x']};
  border: none;
  background-color: transparent;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.2s ease;
  outline: none;

  &:first-child {
    border-top-left-radius: ${({ theme }) => theme.radii.primary};
    border-top-right-radius: ${({ theme }) => theme.radii.primary};
  }

  &:last-child {
    border-bottom-left-radius: ${({ theme }) => theme.radii.primary};
    border-bottom-right-radius: ${({ theme }) => theme.radii.primary};
  }

  ${({ $isSelected, theme }) =>
    $isSelected &&
    `
    background-color: ${theme.background.tertiary};
  `}

  ${({ $isFocused, theme }) =>
    $isFocused &&
    `
    background-color: ${theme.background.tertiary};
  `}

  &:hover {
    background-color: ${({ theme }) => theme.background.tertiary} !important;
  }
`;

const ServiceTypeDropdown = ({
  options,
  value,
  onChange,
  iconColor,
  focusedIndex,
  getOptionId,
}: Props): JSX.Element => {
  const handleOptionClick = (optionValue: ServiceTypeSlug): void => {
    onChange(optionValue);
  };

  return (
    <Box backgroundColor="white" borderRadius="primary">
      {options.map((option, index) => {
        const Icon = SERVICE_TYPE_ICONS[option.value];
        const isSelected = option.value === value;
        const isFocused = focusedIndex === index;

        return (
          <OptionButton
            key={option.value}
            id={getOptionId?.(index)}
            onClick={() => handleOptionClick(option.value as ServiceTypeSlug)}
            $isSelected={isSelected}
            $isFocused={isFocused}
            type="button"
            role="option"
            aria-selected={isSelected}
          >
            <Flex alignItems="center" gap="2x" width="100%">
              {Icon && (
                <Box flexShrink="0">
                  <Icon width={24} height={24} fill={iconColor} />
                </Box>
              )}
              <Flex flexDirection="column" flex="1" minWidth="0">
                <TruncatedText
                  size="100"
                  fontWeight="semibold"
                  textColor="primary"
                  textAlign="left"
                >
                  {option.title}
                </TruncatedText>
                {option.subtitle && (
                  <TruncatedText size="100" textColor="secondary" textAlign="left">
                    {option.subtitle}
                  </TruncatedText>
                )}
              </Flex>
            </Flex>
          </OptionButton>
        );
      })}
    </Box>
  );
};

export default ServiceTypeDropdown;
